import React, { useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import i18next from 'i18next';
import toast from 'react-hot-toast';
import { useMyBranches } from './useMyBranches';
import { fetchAuthorizedBranches } from '../utils/authorizedBranches';
import BranchPickDialog from '../components/BranchPickDialog';

/**
 * Hook to pick a branch for new records created while in "All branches" mode.
 * - In single branch mode: resolves the active branch (falling back through
 *   lastRealGarageId, the user's garage, then the authorized list) with no dialog.
 * - In All mode with <= 1 branch: resolves that branch or lastRealGarageId.
 * - In All mode when the branch list fails to load: toasts and resolves null
 *   (cancelled) rather than silently pinning the record to a fallback branch.
 * - In All mode with > 1 branch: prompts the user via BranchPickDialog.
 *
 * State is read with useSelector, the same way the pages using this hook read
 * it: page tests mock react-redux without a ReactReduxContext, so the hook
 * must not depend on the store context.
 */
export default function useBranchFirst() {
  const activeGarageId = useSelector((s) => s.auth?.activeGarageId);
  const user = useSelector((s) => s.auth?.user);
  const { branches, loading } = useMyBranches();
  const [dialogState, setDialogState] = useState(null);
  const resolveRef = useRef(null);

  // Latest values for pickBranch, which runs from event handlers and must
  // read state at call time, not at capture time.
  const activeGarageIdRef = useRef(activeGarageId);
  activeGarageIdRef.current = activeGarageId;
  const userRef = useRef(user);
  userRef.current = user;

  const handlePick = useCallback((id) => {
    try {
      sessionStorage.setItem('mm.lastCreateBranch', id);
    } catch (e) {
      // Storage unavailable
    }
    setDialogState(null);
    if (resolveRef.current) {
      const resolve = resolveRef.current;
      resolveRef.current = null;
      resolve(id);
    }
  }, []);

  const handleCancel = useCallback(() => {
    setDialogState(null);
    if (resolveRef.current) {
      const resolve = resolveRef.current;
      resolveRef.current = null;
      resolve(null);
    }
  }, []);

  const pickBranch = useCallback(async () => {
    // Active branch at call time: the redux value if set, else localStorage
    // 'activeGarageId' (read like components/BranchField.js does).
    let active = activeGarageIdRef.current;
    if (active === null || active === undefined) {
      try {
        active = localStorage.getItem('activeGarageId');
      } catch (e) {
        active = null;
      }
    }

    // active !== 'all' → single-branch mode: resolve with no dialog, from the
    // first source that has a branch.
    if (active !== 'all') {
      let lastReal = null;
      try {
        lastReal = localStorage.getItem('lastRealGarageId');
      } catch (e) {}

      const currentUser = userRef.current;
      const candidate =
        active || lastReal || currentUser?.garage?.id || currentUser?.garage_id;
      if (candidate) {
        return candidate;
      }
      try {
        const list = await fetchAuthorizedBranches();
        return list?.[0]?.id || null;
      } catch (e) {
        return null;
      }
    }

    // If branches are not loaded yet in All mode, await the load before deciding.
    let branchList = branches;
    if (loading || !branchList || branchList.length === 0) {
      try {
        branchList = await fetchAuthorizedBranches();
      } catch (e) {
        // A failed load is not "one branch": a multi-branch user must not get
        // their record pinned to lastRealGarageId without choosing. i18next.t,
        // not useTranslation, for the same no-context reason as useSelector.
        toast.error(i18next.t('jc.transfer.loadFailed'));
        return null;
      }
    }
    if (!Array.isArray(branchList)) {
      branchList = [];
    }

    // active === 'all' and branches.length <= 1 → resolve(branches[0]?.id || localStorage lastRealGarageId || null)
    if (branchList.length <= 1) {
      let lastReal = null;
      try {
        lastReal = localStorage.getItem('lastRealGarageId');
      } catch (e) {}
      return branchList[0]?.id || lastReal || null;
    }

    // otherwise open BranchPickDialog mode="create"; defaultValue = sessionStorage 'mm.lastCreateBranch' if it is in the list, else localStorage 'lastRealGarageId'
    let lastCreate = null;
    try {
      lastCreate = sessionStorage.getItem('mm.lastCreateBranch');
    } catch (e) {}

    let lastReal = null;
    try {
      lastReal = localStorage.getItem('lastRealGarageId');
    } catch (e) {}

    const defaultValue =
      lastCreate && branchList.some((b) => b.id === lastCreate)
        ? lastCreate
        : lastReal || undefined;

    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setDialogState({
        branches: branchList,
        defaultValue,
      });
    });
  }, [branches, loading]);

  const branchDialog = dialogState ? (
    <BranchPickDialog
      open={true}
      mode="create"
      branches={dialogState.branches}
      defaultValue={dialogState.defaultValue}
      onPick={handlePick}
      onCancel={handleCancel}
    />
  ) : null;

  return { pickBranch, branchDialog };
}
